/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import {styled} from '@mui/material/styles';
import {replaceAt} from '../utils/array';
import {getUsernameSuggestions} from '../shared/http/register-account';
import {TextButton} from './TextButton';
import {Icon} from './Icon';

type Props = {
  onSuggestionSelect: (suggestion: string) => void;
  getSuggestions?: (count: number) => Promise<string[]>;
  suggestionCount?: number;
};

const RefreshButton = styled(TextButton)({
  display: 'flex',
  alignItems: 'center',
  color: '#757575',
  fontSize: '16px',
  marginBottom: '12px',
});

const Suggestions = styled('div')<{
  opacity: number;
}>({
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-evenly',
  display: 'flex',
});

const Suggestion = styled(TextButton)({
  cursor: 'pointer',
  border: '1px solid var(--template-color-2)',
  color: 'blue',
  padding: '10px',
  borderRadius: '20px',
  width: '150px',
  height: '40px',
  marginBottom: '12px',
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',

  '&:focus': {
    boxShadow: '0 0 10px var(--template-color-2)',
    outline: 'none',
  },
});

const SuggestionText = styled('div')({
  fontSize: '14px',
  color: 'var(--template-color-2)',
});

const BlankView = styled('div')({
  width: '80px',
  height: '8px',
  borderRadius: '4px',
  opacity: 0.3,
  backgroundColor: 'var(--template-color-2)',
});

const defaultProps = {
  suggestionCount: 4,
  getSuggestions: getUsernameSuggestions,
};

export function UsernameSuggester(givenProps: Props) {
  const props = {...defaultProps, ...givenProps};
  /** Create a set of blank suggestions for use as loading data */
  const blankSuggestions = React.useMemo(
    () => Array.from({length: props.suggestionCount}, () => ''),
    [props.suggestionCount]
  );

  const [suggestions, setSuggestions] =
    React.useState<string[]>(blankSuggestions);

  /** Fetch suggestions, while showing an intermediary state while loading new suggestions */
  const getSuggestions = React.useCallback(
    async (count: number = props.suggestionCount) => {
      setSuggestions(blankSuggestions);
      const newSuggestions = await props.getSuggestions(count);
      setSuggestions(newSuggestions);
    },
    [blankSuggestions, props]
  );

  /** When a suggestion is selected, replace it with a fresh one */
  const onSuggestionSelect = React.useCallback(
    async (suggestion: string) => {
      const index = suggestions.indexOf(suggestion);

      const replaceSuggestion = (value: string) => {
        setSuggestions(prevSuggestions =>
          replaceAt(prevSuggestions, index, value)
        );
      };

      props.onSuggestionSelect(suggestion);

      replaceSuggestion('');
      const [newSuggestion] = await props.getSuggestions(1);
      replaceSuggestion(newSuggestion);
    },
    [suggestions, props]
  );

  React.useEffect(() => {
    getSuggestions();
    // eslint-disable-next-line
  }, []);

  const suggestionsOpacity = suggestions && suggestions.some(Boolean) ? 1 : 0.7;

  return (
    <>
      <RefreshButton onClick={() => getSuggestions()}>
        <Icon
          icon="refresh"
          // @ts-ignore
          style={{marginRight: 10}}
        />
        Refresh suggestions
      </RefreshButton>

      <Suggestions opacity={suggestionsOpacity}>
        {suggestions.map((suggestion: string, i: number) => (
          <Suggestion
            key={i}
            onClick={() => onSuggestionSelect(suggestion)}
            data-testid="UsernameSuggester__Suggestion"
          >
            {!suggestion ? (
              <BlankView />
            ) : (
              <SuggestionText>{suggestion}</SuggestionText>
            )}
          </Suggestion>
        ))}
      </Suggestions>
    </>
  );
}
